import React from 'react';
import { Trophy } from 'lucide-react';
import { TopDonateBy, TopDonateRow, TopDonateStyle } from './top-donate-options';

/**
 * Thẻ bảng xếp hạng donate: dùng cho overlay OBS và bản xem trước trong cài đặt.
 * Ba hạng đầu có huy hiệu số màu vàng, bạc, đồng; các hạng sau huy hiệu trung tính.
 */

const STYLE: Record<
  TopDonateStyle,
  { card: React.CSSProperties; row: string; muted: string; badge: string; text: string }
> = {
  dark: {
    card: { background: 'rgba(11,18,32,0.82)', backdropFilter: 'blur(8px)' },
    row: 'bg-white/5',
    muted: 'text-white/60',
    badge: 'bg-white/15 text-white',
    text: 'text-white',
  },
  light: {
    card: { background: 'rgba(255,255,255,0.94)', boxShadow: '0 8px 24px rgba(18,28,42,0.18)' },
    row: 'bg-[#F7F9FC]',
    muted: 'text-[#627184]',
    badge: 'bg-[#EFF7FD] text-[#215C91]',
    text: 'text-[#121C2A]',
  },
  clear: {
    card: { background: 'transparent', textShadow: '0 1px 3px rgba(0,0,0,0.85)' },
    row: '',
    muted: 'text-white/80',
    badge: 'bg-black/40 text-white',
    text: 'text-white',
  },
};

const MEDAL = [
  'bg-[#F5B301] text-[#3B2A00]',
  'bg-[#C9D1DB] text-[#233040]',
  'bg-[#C9824A] text-white',
];

const fmt = (n: number) => Math.max(0, Math.round(n || 0)).toLocaleString('vi-VN');

export interface TopDonateBoardProps {
  title: string;
  rows: TopDonateRow[];
  by: TopDonateBy;
  style: TopDonateStyle;
  /** Đổi giá trị này để dòng hạng 1 nảy nhẹ (khi vừa có donate mới). */
  animateKey?: number;
}

const TopDonateBoard: React.FC<TopDonateBoardProps> = ({ title, rows, by, style, animateKey }) => {
  const s = STYLE[style] || STYLE.dark;
  return (
    <div className={`w-full rounded-2xl p-4 ${s.text}`} style={s.card} data-testid="top-donate-board">
      <div className="mb-3 flex items-center gap-2">
        <Trophy size={20} className="shrink-0 text-[#F5B301]" />
        <span className="truncate text-lg font-bold">{title}</span>
      </div>
      {rows.length === 0 ? (
        <p className={`py-2 text-sm ${s.muted}`}>Chưa có ai donate. Hãy là người đầu tiên!</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {rows.map((row, i) => (
            <li
              // Hạng 1 đổi key theo animateKey để dựng lại, hiệu ứng nảy chạy lại mỗi lần có donate mới.
              key={i === 0 ? `${row.userId}-${animateKey || 0}` : row.userId || i}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 ${s.row} ${
                i === 0 && animateKey ? 'animate-[bounce_0.6s_ease-in-out_1]' : ''
              }`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  MEDAL[i] || s.badge
                }`}
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-base font-semibold">{row.name}</span>
              <span className="shrink-0 text-base font-bold tabular-nums">
                {fmt(row.value)} {by === 'count' ? 'lượt' : 'Ca'}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default TopDonateBoard;
